import { Box, VStack, Text, Divider, Center, Spinner } from "@chakra-ui/react";
import { useEffect } from "react";
import { Artifact, LunaBlocksArtifact, SubagentArtifact } from "services/luna/lunaApi";
import { useLunaStore, useLunaMessages } from "state/luna";
import { BlockRenderer, UIBlock } from "../blocks";
import { LunaMessageThread } from "../LunaMessageThread";

/**
 * Artifact Content Renderers
 *
 * This module provides a pattern for rendering different artifact types.
 * Each artifact type has its own renderer component.
 *
 * To add a new artifact type:
 * 1. Add the type to the Artifact union in lunaApi.ts
 * 2. Create a renderer component below
 * 3. Add a case in the ArtifactContentRenderer switch
 */

// Luna Blocks Artifact Renderer
const LunaBlocksRenderer = ({ artifact }: { artifact: LunaBlocksArtifact }) => {
  return (
    <Box
      bg="bg.page"
      borderRadius="container.md"
      boxShadow="0 0 1px 0 rgba(0, 0, 0, 0.32), 0 0 2px 0 rgba(0, 0, 0, 0.08), 0 1px 3px 0 rgba(0, 0, 0, 0.08)"
      p="2xl"
    >
      {/* Title section */}
      <VStack align="start" spacing="lg" mb="xl">
        <Text fontFamily="heading" fontSize="xl" color="text.body.primary">
          {artifact.title}
        </Text>
        {artifact.description && (
          <Text fontSize="md" color="text.label.default">
            {artifact.description}
          </Text>
        )}
      </VStack>

      <Divider borderColor="border.default" mb="xl" />

      {/* Blocks content - flatten nested arrays (content is [[block], [block], ...]) */}
      <VStack align="stretch" spacing="md">
        {artifact.content.flat().map((block, index) => (
          <BlockRenderer key={block.id || index} block={block as UIBlock} />
        ))}
      </VStack>
    </Box>
  );
};

// Unsupported artifact type fallback
const UnsupportedArtifactRenderer = ({ artifact }: { artifact: Artifact }) => {
  return (
    <Center py="xl">
      <Text color="text.body.tertiary">
        Unsupported artifact type: {artifact.type}
      </Text>
    </Center>
  );
};

// Subagent Artifact Renderer - live read-only session view
const SubagentRenderer = ({ artifact }: { artifact: SubagentArtifact }) => {
  const subagentSessionId = artifact.content?.[0]?.session_id ?? null;
  const connectToSession = useLunaStore((s) => s.connectToSession);
  const disconnectFromSession = useLunaStore((s) => s.disconnectFromSession);
  const messages = useLunaMessages(subagentSessionId);

  console.log("[SubagentRenderer] render", {
    subagentSessionId,
    content: artifact.content,
    messageCount: messages.length,
  });

  useEffect(() => {
    if (!subagentSessionId) {
      console.log("[SubagentRenderer] no session id, skipping connect");
      return;
    }

    console.log("[SubagentRenderer] connecting to session:", subagentSessionId);
    connectToSession(subagentSessionId).catch((err) => {
      console.error("[SubagentRenderer] connection failed:", err);
    });

    return () => {
      console.log("[SubagentRenderer] disconnecting from session:", subagentSessionId);
      disconnectFromSession(subagentSessionId);
    };
  }, [subagentSessionId, connectToSession, disconnectFromSession]);

  const isLoading = messages.length === 0;

  return (
    <Box position="relative" h="100%" mx="lg" bg="bg.page">
      {isLoading ? (
        <Center h="100%">
          <Spinner size="lg" color="p.600" />
        </Center>
      ) : (
        <LunaMessageThread sessionId={subagentSessionId} readOnly px="xl" pt="xl" />
      )}
    </Box>
  );
};

// Main renderer that switches on artifact type
export const ArtifactContentRenderer = ({ artifact }: { artifact: Artifact }) => {
  console.log("[ArtifactContentRenderer] artifact type:", artifact.type, "content:", artifact.content);
  switch (artifact.type) {
    case "luna_blocks":
      return <LunaBlocksRenderer artifact={artifact} />;
    case "subagent":
      return <SubagentRenderer artifact={artifact} />;
    default:
      // TypeScript will catch unhandled types at compile time
      // This fallback handles any runtime edge cases
      return <UnsupportedArtifactRenderer artifact={artifact} />;
  }
};
